import { ChakraProvider } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { AdminSampleView } from '@resistapp/client/views/admin-view/admin-sample-view';
import { AdminSamplingPointView } from '@resistapp/client/views/admin-view/admin-sampling-point-view';
import { MetricMode } from '@resistapp/common/types';
import { oldDemoProjectIds } from '@resistapp/common/utils';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import 'mapbox-gl/dist/mapbox-gl.css';
import { StrictMode, useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import {
  Navigate,
  NavigationType,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useLocation,
  useNavigationType,
  useParams,
} from 'react-router-dom';
import './ag-grid-enterprise';
import { MobileSupportNotice } from './components/misc/mobile-support-notice';
import { Footer } from './components/page-layout/footer/footer';
import { HeaderBar } from './components/page-layout/header-bar/header-bar';
import { storedToken } from './components/shared/local-storage';
import { chakraTheme, theme } from './components/shared/theme';
import { AssayProvider } from './contexts/assay-context';
import { AnalyticsProvider } from './contexts/analytics-context';
import { ResearchProvider } from './contexts/research-context';
import { SampleDataProvider } from './contexts/sample-data-context';
import { SearchParamsProvider } from './contexts/search-params-context';
import { UrlProvider } from './contexts/url-provider';
import { OverviewContextProvider } from './contexts/use-overview-context/use-overview-context';
import { UserContextProvider, useUser } from './contexts/use-user-context';
import { SetGlobalStyles } from './global-styles';
import { useEnsureOrganisationSampleSheet } from './hooks/api';
import { useLabWorkbookLanding } from './hooks/use-lab-workbook-landing';
import { useNavigateWithQuery } from './hooks/use-navigate-with-query';
import { PathParams, usePathParam } from './hooks/use-path-params';
import { initHotjar } from './hotjar';
import { getAdminIndexPath, shouldForceLabLanding } from './utils/access';
import './utils/sentry';
import { PageNotFoundView } from './views/PageNotFoundView';
import { RootErrorView } from './views/RootErrorView';
import { AdminChipsView } from './views/admin-view/admin-chips-view';
import { AdminLabWorkbooksView } from './views/admin-view/admin-lab-workbooks-view';
import { AdminLayout } from './views/admin-view/admin-layout';
import { AdminOrganisationView } from './views/admin-view/admin-organisation-view';
import { AdminOrganisationsView } from './views/admin-view/admin-organisations-view';
import { AdminProjectsView } from './views/admin-view/admin-projects-view';
import { AdminShipmentEditView } from './views/admin-view/admin-shipment-edit-view';
import {
  AdminRawShipmentPlaceholderView,
  AdminShipmentRequestTypeView,
  WorkbookShipmentRequestTypeView,
} from './views/admin-view/admin-shipment-request-type-view';
import { AdminShipmentReadView } from './views/admin-view/admin-shipment-read-view';
import { AdminShipmentView } from './views/admin-view/admin-shipment-view';
import { AdminShippingView } from './views/admin-view/admin-shipping-view';
import { AdminSamplesView } from './views/admin-view/admin-samples-view';
import { AdminSamplingPointsView } from './views/admin-view/admin-sampling-points-view';
import { AdminSensorsView } from './views/admin-view/admin-sensors-view';
import { AdminUsersView } from './views/admin-view/admin-users-view';
import { MergeSamplingPointsView } from './views/admin-view/merge-sampling-points-view';
import { AssayChangeLogView } from './views/assay-change-log-view/assay-change-log-view';
import { ChipView } from './views/chip-view/chip-view';
import { ComposeView } from './views/compose-view/compose-view';
import {
  DnaExtractionPage,
  LabLayout,
  LabWorkbookIndexRedirect,
  PrimerPrepPage,
  SampleAssayPage,
  SamplePlatesPage,
  SmartChipRunCtsTablePage,
  SmartChipRunPage,
} from './views/lab-view';
import { LanderView } from './views/lander-view';
import { LoginView } from './views/login-view';
import { CreateEmptyProjectView } from './views/organisations-view/create-empty-project-view';
import { CreateOrganisationView } from './views/organisations-view/create-organisation-view';
import { CreateUserView } from './views/organisations-view/create-user-view';
import { OverviewView } from './views/overview-view/overview-view';
import { PasswordResetView } from './views/password-reset-view';
import { ReproducibilityView } from './views/reproducibility/reproducibility-view';
import { ResearchView } from './views/research-view/research-view';
import { UserShippingView } from './views/shipment-view/user-shipping-view';
import { WorkbookShipmentView } from './views/shipment-view/workbook-shipment-view';
import { InvitedSignupView } from './views/signup/invited-signup-view';
import { SignupView } from './views/signup/signup-view';
import { VerifyEmailView } from './views/signup/verify-email-view';
import { WorldView } from './views/world-view/world-view';

initHotjar();

const rootElement = document.createElement('div');
rootElement.id = 'content';
document.body.appendChild(rootElement);

const root = document.getElementById('root');
if (!root) {
  throw Error('Root element not found!');
}

const queryClient = new QueryClient();

function Layout({ children }: { children?: React.ReactNode }) {
  return (
    <ChakraProvider theme={chakraTheme}>
      <SetGlobalStyles />
      <ScrollToTopOnRouteChange />
      <QueryClientProvider client={queryClient}>
        <UserContextProvider>
          <UrlProvider>
            <AssayProvider>
              <SearchParamsProvider>
                <AnalyticsProvider>
                  <MobileSupportNotice />
                  <HeaderBar />
                  {children ?? (
                    <ZindexPositioner>
                      <Outlet />
                    </ZindexPositioner>
                  )}
                  {/* this div-element positions the main content on top, instead of center */}
                  <div style={{ flexGrow: 1 }} />
                  <Footer />
                </AnalyticsProvider>
              </SearchParamsProvider>
            </AssayProvider>
          </UrlProvider>
        </UserContextProvider>
      </QueryClientProvider>
    </ChakraProvider>
  );
}

function ScrollToTopOnRouteChange() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const previousPathnameRef = useRef(pathname);

  useLayoutEffect(() => {
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = pathname;
    if (previousPathname === pathname || navigationType === NavigationType.Pop) {
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, navigationType]);

  return null;
}

function OldProjectRedirect({ children }: { children: React.ReactNode }) {
  const { projectId } = useParams();
  const oldIds = Object.values(oldDemoProjectIds);
  const { user, userLoading } = useUser();
  const token = storedToken();

  if (projectId && oldIds.includes(+projectId) && !token && !userLoading && !user) {
    window.location.href = 'https://www.resistomap.com/demo-contact';
  }

  return <>{children}</>;
}

function RedirectToResearch() {
  const projectId = usePathParam(PathParams.projectId);
  return <Navigate to={`/research/${projectId}`} replace />;
}

function LegacyShippingProjectRedirect() {
  return <Navigate to="/admin/sample-shipping" replace />;
}

function LegacyShippingWorkbookRedirect() {
  const { workbookId } = useParams<{ workbookId: string }>();
  return (
    <Navigate to={workbookId ? `/admin/sample-shipping/workbook/${workbookId}` : '/admin/sample-shipping'} replace />
  );
}

function LegacySampleSheetRedirect() {
  return <LegacyLabRedirect />;
}

function LegacyGenePanelRedirect() {
  return <LegacyLabRedirect />;
}

function LegacyLabRedirect() {
  const { organisationId } = useParams<{ organisationId?: string }>();
  const navigate = useNavigateWithQuery();

  const parsedId = organisationId ? Number(organisationId) : NaN;
  const resolvedOrgId = Number.isFinite(parsedId) ? parsedId : null;

  const ensureQuery = useEnsureOrganisationSampleSheet(resolvedOrgId, {
    enabled: resolvedOrgId != null,
  });

  useEffect(() => {
    if (resolvedOrgId == null || !ensureQuery.isSuccess) {
      return;
    }

    const workbookId = ensureQuery.data.labWorkbookId;
    if (!workbookId) {
      navigate(`/admin/organisation/${resolvedOrgId}`, false);
      return;
    }

    navigate(`/lab/${workbookId}`, false);
  }, [ensureQuery.data, ensureQuery.isSuccess, navigate, resolvedOrgId]);

  if (resolvedOrgId == null) {
    return <Navigate to="/admin" replace />;
  }

  if (ensureQuery.isError) {
    return <Navigate to={`/admin/organisation/${resolvedOrgId}`} replace />;
  }

  return null;
}

function LegacySmartchipSpecRedirect() {
  const { workbookId } = useParams<{ workbookId?: string }>();

  if (!workbookId) {
    return <Navigate to="/admin" replace />;
  }

  return <Navigate to={`/lab/${workbookId}`} replace />;
}

function LegacyDnaExtractionRedirect() {
  const { workbookId } = useParams<{ workbookId?: string }>();

  if (!workbookId) {
    return <Navigate to="/admin" replace />;
  }

  return <Navigate to={`/lab/${workbookId}/dna-qa`} replace />;
}

function AdminIndexRedirect() {
  const { user, userLoading } = useUser();
  const forceLabLanding = shouldForceLabLanding(user);
  const {
    path: labLandingPath,
    organisationId: labOrgId,
    ensureQuery: labLandingQuery,
  } = useLabWorkbookLanding(user, {
    enabled: forceLabLanding,
  });
  const labLandingPending = Boolean(forceLabLanding && labOrgId && labLandingQuery.isPending);
  if (userLoading) {
    return null; // wait for user to load to avoid flicker
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (labLandingPending) {
    return null;
  }
  if (forceLabLanding && labLandingPath) {
    return <Navigate to={labLandingPath} replace />;
  }
  return <Navigate to={getAdminIndexPath(user)} replace />;
}

const router = createBrowserRouter([
  {
    element: <Layout />,
    errorElement: (
      <Layout>
        <RootErrorView />
      </Layout>
    ),
    children: [
      {
        path: '/',
        element: <LanderView />,
        // TODO loader?
      },
      {
        path: '/world',
        element: <WorldView />,
      },
      {
        path: `/signup/:${PathParams.signupCode}`,
        element: <SignupView />,
      },
      {
        path: `/signup/invite/:${PathParams.inviteCode}`,
        element: <InvitedSignupView />,
      },
      {
        path: `/verify-email`,
        element: <VerifyEmailView />,
      },
      {
        path: '/reset-password',
        element: <PasswordResetView />,
      },
      {
        path: '/login',
        element: <LoginView />,
      },
      {
        path: '/user',
        element: <CreateUserView />,
      },
      {
        path: '/samplings',
        element: <Navigate to="/" replace />,
      },
      {
        path: '/organisation/create',
        element: <CreateOrganisationView />,
      },
      {
        path: '/admin',
        element: <AdminLayout />,
        children: [
          { index: true, element: <AdminIndexRedirect /> },
          { path: 'organisations', element: <AdminOrganisationsView /> },
          { path: 'organisation/:id', element: <AdminOrganisationView /> },
          { path: 'users', element: <AdminUsersView /> },
          { path: 'projects', element: <AdminProjectsView /> },
          { path: 'sampling-points', element: <AdminSamplingPointsView /> },
          { path: 'sampling-point/:id', element: <AdminSamplingPointView /> },
          { path: 'samples', element: <AdminSamplesView /> },
          { path: 'sample/:id', element: <AdminSampleView /> },
          { path: 'chips', element: <AdminChipsView /> },
          { path: 'sensors', element: <AdminSensorsView /> },
          { path: 'lab-workbooks', element: <AdminLabWorkbooksView /> },
          { path: 'shipment', element: <AdminShipmentRequestTypeView /> },
          { path: 'shipment/dna', element: <AdminShipmentView /> },
          { path: 'shipment/raw', element: <AdminRawShipmentPlaceholderView /> },
          { path: 'shipment/:id/view', element: <AdminShipmentReadView /> },
          { path: 'shipment/:id', element: <AdminShipmentEditView /> },
          { path: 'shipping', element: <AdminShippingView /> },
          {
            path: `shipping/project/:${PathParams.projectId}`,
            element: <Navigate to="/admin/shipment/dna" replace />,
          },
          { path: 'sample-shipping', element: <UserShippingView /> },
          {
            path: `sample-shipping/project/:${PathParams.projectId}`,
            element: <Navigate to="/admin/sample-shipping" replace />,
          },
          { path: 'sample-shipping/workbook/:workbookId', element: <WorkbookShipmentRequestTypeView /> },
          { path: 'sample-shipping/workbook/:workbookId/dna', element: <WorkbookShipmentView /> },
          { path: 'shipments', element: <Navigate to="/admin/shipping" replace /> },
          { path: 'merge-sampling-points', element: <MergeSamplingPointsView /> },
        ],
      },
      {
        path: '/lab/:workbookId',
        element: <LabLayout />,
        children: [
          { index: true, element: <LabWorkbookIndexRedirect /> },
          { path: 'gene-template', element: <SampleAssayPage /> },
          { path: 'sample-spec', element: <SampleAssayPage /> },
          { path: 'dna-qa', element: <DnaExtractionPage /> },
          { path: 'dna-extraction', element: <LegacyDnaExtractionRedirect /> },
          { path: 'smartchip-spec', element: <LegacySmartchipSpecRedirect /> },
          { path: 'primer-prep', element: <PrimerPrepPage /> },
          { path: 'sample-plates', element: <SamplePlatesPage /> },
          { path: 'smartchip-run', element: <SmartChipRunPage /> },
          { path: 'smartchip-run/:chipId/cts-table', element: <SmartChipRunCtsTablePage /> },
        ],
      },
      {
        path: `/sampling/:${PathParams.projectId}`,
        element: <RedirectToResearch />,
      },
      {
        path: '/analyse',
        element: <Navigate to="/admin" replace />,
      },
      {
        path: '/smartchip-spec/:organisationId?',
        element: <LegacySampleSheetRedirect />,
      },
      {
        path: '/sample-sheet/:organisationId',
        element: <LegacySampleSheetRedirect />,
      },
      {
        path: '/gene-panel',
        element: <LegacyGenePanelRedirect />,
      },
      {
        path: '/gene-panel/:organisationId',
        element: <LegacyGenePanelRedirect />,
      },
      {
        path: '/compose',
        element: <ComposeView />,
      },
      {
        path: '/reproducibility',
        element: <ReproducibilityView />,
      },
      {
        path: '/assay-log',
        element: <AssayChangeLogView />,
      },
      {
        path: '/project/create-empty',
        element: <CreateEmptyProjectView />,
      },
      {
        path: '/shipping',
        element: <Navigate to="/admin/sample-shipping" replace />,
      },
      {
        path: `/shipping/project/:${PathParams.projectId}`,
        element: <LegacyShippingProjectRedirect />,
      },
      {
        path: '/shipping/workbook/:workbookId',
        element: <LegacyShippingWorkbookRedirect />,
      },
      {
        element: (
          <SampleDataProvider>
            <Outlet />
          </SampleDataProvider>
        ),
        children: [
          {
            path: `/index/:${PathParams.projectId}?`,
            element: (
              <OldProjectRedirect>
                <OverviewContextProvider metricMode={MetricMode.ARGI}>
                  <OverviewView />
                </OverviewContextProvider>
              </OldProjectRedirect>
            ),
          },
          {
            path: `/risk/:${PathParams.projectId}?`,
            element: (
              <OldProjectRedirect>
                <OverviewContextProvider metricMode={MetricMode.RISK}>
                  <OverviewView />
                </OverviewContextProvider>
              </OldProjectRedirect>
            ),
          },
          {
            path: '/reduction/:projectId?',
            element: (
              <OldProjectRedirect>
                <OverviewContextProvider metricMode={MetricMode.REDUCTION}>
                  <OverviewView />
                </OverviewContextProvider>
              </OldProjectRedirect>
            ),
          },
          {
            path: `/research/:${PathParams.projectId}`,
            element: (
              <OldProjectRedirect>
                <ResearchProvider>
                  <ResearchView />
                </ResearchProvider>
              </OldProjectRedirect>
            ),
          },
          {
            path: `/overview/:${PathParams.projectId}?`,
            element: <Navigate to={location.pathname.replace(/^\/overview\//, '/index/')} replace />,
          },
        ],
      },
      {
        path: '/chip/:chipId',
        element: <ChipView />,
      },
      {
        path: `/shipment/project/:${PathParams.projectId}`,
        element: <LegacyShippingProjectRedirect />,
      },
      {
        path: '*',
        element: <PageNotFoundView />,
      },
    ],
  },
]);

ReactDOM.createRoot(root).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);

const ZindexPositioner = styled.div`
  z-index: ${theme.zIndexes.mainSection};
  position: relative;
`;
